/**
 * SendCardModal
 * Shared modal for sending a business card email to a lead or client.
 * Rendered conditionally by the parent — unmount to close.
 *
 * Two things here are deliberate and worth not undoing:
 *
 * 1. **Cards are grouped and the default is a rule, not an accident.** The list
 *    used to be flat and the API returned it unordered, so "the first personal
 *    card" could be a different card on two loads of the same page. The API now
 *    orders, and the default prefers a card the signed-in user actually owns.
 * 2. **Do Not Contact warns rather than blocks.** A person sending by hand can
 *    see context the suppression list cannot, so the warning names what matched
 *    and offers both ways forward — send anyway, or lift the suppression. The
 *    API refuses unless `acknowledge_dnc` is set, so this is a real gate and not
 *    a decorative banner.
 */
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Modal from './Modal'
import {
  listBusinessCards,
  sendBusinessCardToLead,
  sendBusinessCardToClient,
  sendBusinessCardToAddress,
  getClientDncStatus,
  getLeadDncStatus,
  getEmailDncStatus,
  toggleDnc,
} from '../services/crm'
import { useAuth } from '../services/auth'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { pickDefaultCard } from '../utils/businessCardForm'
import type { BusinessCardTemplate, DncStatus } from '../types'

interface Props {
  onClose: () => void
  /** If set, sends to this lead and logs a lead note */
  leadId?: number
  /** If set, sends to this client and logs a communication entry */
  clientId?: number
  /**
   * Send this specific card, with no picker — used from the business-card pages,
   * where the card is the thing you clicked. With neither `leadId` nor
   * `clientId`, the send goes to a plain address and there is no CRM record to
   * log against or to resolve against the Do Not Contact list.
   */
  fixedCard?: BusinessCardTemplate
  /** Pre-filled recipient email (editable) */
  defaultEmail?: string
  /** Pre-filled subject line (editable) */
  defaultSubject?: string
  /** Called after a successful send */
  onSent?: () => void
}

export default function SendCardModal({
  onClose,
  leadId,
  clientId,
  fixedCard,
  defaultEmail = '',
  defaultSubject = '',
  onSent,
}: Props) {
  const { user } = useAuth()

  const [cards, setCards] = useState<BusinessCardTemplate[]>([])
  const [loadingCards, setLoadingCards] = useState(true)

  const [selectedCardId, setSelectedCardId] = useState<number | null>(null)
  const [toEmail, setToEmail] = useState(defaultEmail)
  const [subject, setSubject] = useState(defaultSubject)
  const [personalNote, setPersonalNote] = useState('')

  const [dnc, setDnc] = useState<DncStatus | null>(null)
  const [dncAcknowledged, setDncAcknowledged] = useState(false)
  const [liftingDnc, setLiftingDnc] = useState(false)

  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<{ sent: boolean; message: string } | null>(null)
  const [error, setError] = useState('')

  // The list can now match the address as well as the record, so the warning
  // follows what is typed rather than being read once when the dialog opens.
  // Debounced because it fires a request and the box changes per keystroke.
  const debouncedToEmail = useDebouncedValue(toEmail)

  useEffect(() => {
    loadCards()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // An acknowledgement is about the suppression on screen; retyping the
    // address asks a different question, so the tick must not carry over.
    setDncAcknowledged(false)
    loadDncStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedToEmail])

  async function loadCards() {
    // Nothing to choose when the caller already picked the card.
    if (fixedCard) {
      setCards([fixedCard])
      setSelectedCardId(fixedCard.id)
      setLoadingCards(false)
      return
    }
    setLoadingCards(true)
    try {
      const all = await listBusinessCards()
      const active = all.filter((c) => c.is_active)
      setCards(active)
      setSelectedCardId(pickDefaultCard(active, user?.id)?.id ?? null)
    } catch {
      setCards([])
      setSelectedCardId(null)
    } finally {
      setLoadingCards(false)
    }
  }

  async function loadDncStatus() {
    // With a record, the license-shaped match types resolve through it and the
    // address is checked as well. With no record — a card sent to a typed
    // address — only the `email` match type can answer, which is a narrower
    // question than the other two but no longer no question at all.
    const email = debouncedToEmail.trim()
    try {
      if (leadId !== undefined) {
        setDnc(await getLeadDncStatus(leadId, email || undefined))
      } else if (clientId !== undefined) {
        setDnc(await getClientDncStatus(clientId, email || undefined))
      } else if (email.includes('@')) {
        setDnc(await getEmailDncStatus(email))
      } else {
        setDnc(null)
      }
    } catch {
      // A failed check must not block sending — the API re-checks and is the
      // real gate. Leaving this null simply means no warning is shown.
      setDnc(null)
    }
  }

  /** Lift one suppression from inside the warning, then re-check. */
  async function handleLiftDnc(matchType: string, value: string) {
    setLiftingDnc(true)
    setError('')
    try {
      await toggleDnc({ match_type: matchType, value })
      await loadDncStatus()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the Do Not Contact entry')
    } finally {
      setLiftingDnc(false)
    }
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedCardId) { setError('Please select a card'); return }
    if (!toEmail.trim()) { setError('Recipient email is required'); return }

    setSending(true)
    setError('')
    setResult(null)
    try {
      const req = {
        card_id: selectedCardId,
        to_email: toEmail.trim(),
        subject: subject.trim() || undefined,
        personal_note: personalNote.trim() || undefined,
        acknowledge_dnc: dncAcknowledged || undefined,
      }
      const res =
        leadId !== undefined
          ? await sendBusinessCardToLead(leadId, req)
          : clientId !== undefined
            ? await sendBusinessCardToClient(clientId, req)
            : await sendBusinessCardToAddress(selectedCardId, {
                to_email: req.to_email,
                subject: req.subject,
                personal_note: req.personal_note,
                acknowledge_dnc: req.acknowledge_dnc,
              })

      setResult(res)
      if (res.sent) onSent?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed')
    } finally {
      setSending(false)
    }
  }

  const selectedCard = cards.find((c) => c.id === selectedCardId) ?? null
  const companyCards = cards.filter((c) => c.card_type === 'company')
  const personalCards = cards.filter((c) => c.card_type === 'personal')
  const blockedByDnc = Boolean(dnc?.suppressed) && !dncAcknowledged

  return (
    <Modal
      titleId="send-card-title"
      title={fixedCard ? `Send: ${fixedCard.name}` : 'Send Business Card'}
      onClose={onClose}
      showCloseButton
    >
      {loadingCards ? (
        <div className="text-gray-500 py-4">Loading cards…</div>
      ) : cards.length === 0 ? (
        <div className="text-gray-500 py-4">
          No business cards found.{' '}
          <Link to="/profile/business-cards" className="text-blue-600 underline" onClick={onClose}>
            Create one in your profile.
          </Link>
        </div>
      ) : result ? (
        <div className="py-2">
          <div className={`alert ${result.sent ? 'alert-success' : 'alert-error'} mb-4`}>
            {result.message}
          </div>
          <div className="flex justify-end">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSend} className="space-y-4">

          {dnc?.suppressed && (
            <div className="alert alert-warning" role="alert">
              <p className="font-medium">
                This contact asked not to be contacted.
              </p>
              <p className="text-sm mt-1">
                Marked as do not contact{dnc.matched_on ? ` — ${dnc.matched_on}` : ''}.
                {dnc.reason ? ` Reason given: ${dnc.reason}` : ''}
              </p>
              <div className="flex flex-wrap gap-3 mt-2 items-center">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={dncAcknowledged}
                    onChange={(e) => setDncAcknowledged(e.target.checked)}
                  />
                  Send anyway
                </label>
                {dnc.matches.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={liftingDnc}
                    onClick={() => handleLiftDnc(m.match_type, m.value)}
                  >
                    {liftingDnc ? 'Removing…' : `Remove do-not-contact for ${m.value}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Card selector, grouped so the two kinds are not interleaved.
              Hidden when the caller already chose the card — offering a picker
              that reselects what you just clicked is noise. */}
          {!fixedCard && (
            <div>
              <label className="label">Business Card *</label>
              <div className="space-y-3">
                {personalCards.length > 0 && (
                  <CardGroup
                    heading="Yours"
                    cards={personalCards}
                    selectedCardId={selectedCardId}
                    onSelect={setSelectedCardId}
                  />
                )}
                {companyCards.length > 0 && (
                  <CardGroup
                    heading="Company"
                    cards={companyCards}
                    selectedCardId={selectedCardId}
                    onSelect={setSelectedCardId}
                  />
                )}
              </div>
            </div>
          )}

          {/* Recipient */}
          <div>
            <label className="label">Send To *</label>
            <input
              type="email"
              className="input"
              required
              maxLength={255}
              placeholder="recipient@example.com"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
            />
          </div>

          {/* Subject (optional) */}
          <div>
            <label className="label">
              Subject <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              className="input"
              maxLength={200}
              placeholder={selectedCard ? `Hello from ${selectedCard.sender_name}…` : 'Leave blank for default'}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          {/* Personal note */}
          <div>
            <label className="label">
              Personal Note{' '}
              <span className="text-gray-400 font-normal">(optional — shown above the card)</span>
            </label>
            <textarea
              className="input min-h-[70px]"
              maxLength={1000}
              placeholder="Add a personal message…"
              value={personalNote}
              onChange={(e) => setPersonalNote(e.target.value)}
            />
            {selectedCard?.greeting_text && !personalNote && (
              <p className="text-xs text-gray-400 mt-1">
                Card default greeting: &ldquo;{selectedCard.greeting_text.slice(0, 80)}
                {selectedCard.greeting_text.length > 80 ? '…' : ''}&rdquo;
              </p>
            )}
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          <div className="flex justify-end gap-3">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending || !selectedCardId || blockedByDnc}
              className="btn btn-primary"
            >
              {sending ? 'Sending…' : 'Send Card'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

function CardGroup({
  heading,
  cards,
  selectedCardId,
  onSelect,
}: {
  heading: string
  cards: BusinessCardTemplate[]
  selectedCardId: number | null
  onSelect: (id: number) => void
}) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
        {heading}
      </p>
      <div className="space-y-2">
        {cards.map((card) => (
          <label
            key={card.id}
            className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition-colors ${
              selectedCardId === card.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="card"
              value={card.id}
              checked={selectedCardId === card.id}
              onChange={() => onSelect(card.id)}
              className="mt-0.5"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm text-gray-900">{card.sender_name}</span>
                {card.title && <span className="text-xs text-gray-500 italic">{card.title}</span>}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">{card.name}</div>
              {(card.email || card.phone) && (
                <div className="text-xs text-gray-500 mt-1">
                  {card.email && <span>{card.email}</span>}
                  {card.email && card.phone && <span className="mx-1">·</span>}
                  {card.phone && <span>{card.phone}</span>}
                </div>
              )}
            </div>
          </label>
        ))}
      </div>
    </div>
  )
}
